<template>
	<div :class="[seriesClasses, { 'series--short': shortSeries }]">
		<h2 class="overline mb-50">
			{{ series.seriesLabel ?? 'Series' }}
		</h2>
		<h3 class="heading-2 mb-50 mt-50">{{ series.seriesHeading }}</h3>
		<p v-if="series.seriesSubheading" class="body-md mt-0 mb-100">
			{{ series.seriesSubheading }}
			<BaseCtaLink
				v-if="series.subheadingLink && series.subheadingLinkText"
				:link="{ href: series.subheadingLink }"
				:text="series.subheadingLinkText"
				icon="arrow"
			/>
		</p>
		<div v-if="seriesArticles" class="mt-100 gutters-300-md grid">
			<article
				v-for="(featured, featuredId) in seriesArticles.slice(0, 1)"
				:key="featuredId"
				:class="[shortSeries ? 'col-8-lg' : 'col-4-lg']"
				class="article-list-item col-12 col-6-md mb-200-smo"
			>
				<BaseLink
					v-if="featured.slug.current"
					:classes="featuredSeriesClasses"
					:link="{ router: 'blog-post', params: { blogPostUrlSlug: featured.slug.current } }"
				>
					<div :class="{ 'col-12 col-6-md': shortSeries }">
						<figure
							v-if="featured.coverImage"
							role="presentation"
							class="mt-0 ml-0"
							:class="[shortSeries ? 'mb-0 mr-10' : 'mr-0']"
						>
							<ArticleCoverImage :cover-image="featured.coverImage.asset" />
						</figure>
					</div>
					<div :class="{ 'col-12 col-6-md': shortSeries }">
						<h2 class="article-title heading-5 mb-50 mt-50">
							{{ featured.title }}
						</h2>
						<p v-if="featured.abstract" class="mt-50" v-html="featured.abstract"></p>
					</div>
				</BaseLink>
			</article>
			<div :class="[shortSeries ? 'col-4-lg' : 'col-8-lg']" class="col-12 col-6-md">
				<div class="display-flex align-items-stretch flex-wrap-wrap justify-content-space-between">
					<div
						v-for="(article, articleId) in seriesArticles.slice(1, 5)"
						:key="articleId"
						class="flex-article"
					>
						<ArticleCard :article="article" :base-link-classes="'article-list-item-inner'" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { SanityArticlesSeries, SanityArticleTeaser } from 'types/sanity/education-center';
import ArticleCard from '@components/blog/article-card.vue';
import ArticleCoverImage from '@components/blog/article-cover-image.vue';
import { computed } from 'vue';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';

interface Props {
	series: SanityArticlesSeries;
	seriesClasses?: string;
	showDate?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	seriesClasses: 'article-list--series container pt-150 pt-300-md pb-300'
});

const seriesArticles: SanityArticleTeaser[] = mapInvestorUpdateArticleTeasers(props.series.seriesPosts ?? []);

const shortSeries = computed((): boolean => {
	if (seriesArticles && seriesArticles.length < 4) {
		return true;
	}
	return false;
});

const featuredSeriesClasses = computed((): string | undefined => (shortSeries.value ? 'grid gutters-150' : undefined));
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.article-list--series {
	.flex-article {
		width: 100%;
		@include respond-to(lg) {
			width: calc(50% - 1.5rem);
		}
	}
	&.series--short {
		.flex-article {
			width: 100%;
		}
	}
}
</style>
